import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { trackAction } from "@/lib/analyticsTracker";
import { Heart, ShieldCheck, Lock } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Discreet three-option radio row — kept borderless and airy so it fits the
// luxurious wellness aesthetic. `name` is used for the underlying radio group
// so keyboard tab order behaves naturally.
function AnswerRow({ label, name, value, onChange, disabled, testId }) {
  const opts = [
    { v: "no", label: "No" },
    { v: "yes", label: "Yes" },
    { v: "prefer_not", label: "I'd rather not say" },
  ];
  return (
    <div className="space-y-2" data-testid={`${testId}-row`}>
      <div className="text-sm font-medium text-foreground">{label}</div>
      <div className="flex flex-wrap gap-2">
        {opts.map((o) => {
          const active = value === o.v;
          return (
            <label
              key={o.v}
              className={`inline-flex items-center gap-2 px-3 py-2 border cursor-pointer transition-colors ${
                active ? "border-accent bg-accent/10 text-accent" : "border-border bg-background hover:border-accent/40"
              } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
              data-testid={`${testId}-${o.v}`}
            >
              <input
                type="radio"
                name={name}
                value={o.v}
                checked={active}
                onChange={() => onChange(o.v)}
                disabled={disabled}
                className="sr-only"
              />
              <span className="text-sm">{o.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Wellness-preferences dialog. Fetches `/public/guest-health` on open so the
 * form starts pre-filled if the guest has answered before. Saves via PUT and
 * calls `onSaved(profile)` so callers can update their local badge/state.
 *
 * Props:
 *   - open, onOpenChange       — dialog controls
 *   - guestId                  — required, opaque UUID (never a PIN)
 *   - female                   — controls pregnancy row visibility
 *   - onSaved(profile)         — callback after a successful PUT
 *   - dismissible              — when false hides the Skip button (used when
 *                                the guest tried to book without answering)
 */
export default function GuestHealthDialog({ open, onOpenChange, guestId, female, onSaved, dismissible = true }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Whether the guest has previously answered the questionnaire. When true we
  // deliberately DO NOT prefill their prior answers — those are held in
  // confidence for the spa crew only. The guest simply resubmits from scratch.
  const [previouslySubmitted, setPreviouslySubmitted] = useState(false);
  const [form, setForm] = useState({
    allergies_answer: null,
    allergies_text: "",
    medications_answer: null,
    medications_text: "",
    pregnancy_answer: null,
  });

  useEffect(() => {
    if (!open || !guestId) return;
    setLoading(true);
    // Always start with a clean form. We only ask the backend whether a
    // submission exists so we can display the confidentiality note — the
    // actual answers are never returned to the guest.
    setForm({
      allergies_answer: null,
      allergies_text: "",
      medications_answer: null,
      medications_text: "",
      pregnancy_answer: null,
    });
    api.get(`/public/guest-health?guest_id=${encodeURIComponent(guestId)}`)
      .then(({ data }) => setPreviouslySubmitted(!!data?.submitted))
      .catch((e) => console.debug("[GuestHealth] fetch failed:", e))
      .finally(() => setLoading(false));
  }, [open, guestId]);

  const submit = async () => {
    if (!guestId) return;
    // Encourage at least one answer so we can flag as "submitted".
    if (!form.allergies_answer && !form.medications_answer && !form.pregnancy_answer) {
      toast.error("Please answer at least one question, or close and skip.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        guest_id: guestId,
        allergies_answer: form.allergies_answer,
        allergies_text: form.allergies_answer === "yes" ? form.allergies_text : "",
        medications_answer: form.medications_answer,
        medications_text: form.medications_answer === "yes" ? form.medications_text : "",
        pregnancy_answer: female ? form.pregnancy_answer : null,
      };
      const { data } = await api.put("/public/guest-health", body);
      trackAction("menu", "wellness_submitted", { has_allergies: form.allergies_answer === "yes", has_meds: form.medications_answer === "yes" });
      toast.success("Wellness preferences saved · thank you");
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not save preferences");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="guest-health-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Heart className="h-5 w-5 text-accent" /> Wellness preferences
          </DialogTitle>
          <p className="text-sm text-muted-foreground pt-2 leading-relaxed">
            Two quick questions to help your therapist tailor each treatment. Answers stay <span className="text-foreground">private</span> —
            only the crew treating you can see them, and only for the duration of your stay.
          </p>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-5 pt-2">
            {previouslySubmitted && (
              <div
                className="flex gap-3 items-start border border-accent/40 bg-accent/5 p-3 text-sm leading-relaxed"
                data-testid="qh-confidential-note"
              >
                <Lock className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                <div className="text-foreground/85">
                  <div className="font-medium text-foreground">Your answers are held in confidence.</div>
                  <p className="text-muted-foreground mt-1">
                    For your privacy, we don't display previously submitted responses back to you — not even here.
                    Simply answer again if anything has changed, or ask a spa crew member to confirm what's on file for you.
                  </p>
                </div>
              </div>
            )}
            <AnswerRow
              label="Any allergies or conditions we should know about?"
              name="allergies"
              value={form.allergies_answer}
              onChange={(v) => setForm({ ...form, allergies_answer: v })}
              disabled={saving}
              testId="qh-allergies"
            />
            {form.allergies_answer === "yes" && (
              <Textarea
                rows={2}
                value={form.allergies_text}
                onChange={(e) => setForm({ ...form, allergies_text: e.target.value })}
                placeholder="Please share what we should know…"
                disabled={saving}
                className="border-accent/40"
                data-testid="qh-allergies-text"
              />
            )}

            <AnswerRow
              label="Are you currently taking any medication?"
              name="medications"
              value={form.medications_answer}
              onChange={(v) => setForm({ ...form, medications_answer: v })}
              disabled={saving}
              testId="qh-medications"
            />
            {form.medications_answer === "yes" && (
              <Textarea
                rows={2}
                value={form.medications_text}
                onChange={(e) => setForm({ ...form, medications_text: e.target.value })}
                placeholder="Please share what you're taking…"
                disabled={saving}
                className="border-accent/40"
                data-testid="qh-medications-text"
              />
            )}

            {female && (
              <AnswerRow
                label="Are you currently pregnant?"
                name="pregnancy"
                value={form.pregnancy_answer}
                onChange={(v) => setForm({ ...form, pregnancy_answer: v })}
                disabled={saving}
                testId="qh-pregnancy"
              />
            )}

            <div className="flex items-center gap-2 text-[11px] text-muted-foreground pt-1 border-t border-border pt-3">
              <ShieldCheck className="h-3 w-3 text-accent" />
              <span>Your answers are stored privately and only visible to the on-board wellness team.</span>
            </div>

            {/* Two-button footer.
                • Previously-submitted guests get an accent-tinted "Use my
                  saved answers" primary action that closes the dialog
                  and reuses the answers already on file — one tap and
                  they're on their way to the calendar.
                • Fresh guests only see "Save preferences" (accent) plus
                  a subtle "Not now" ghost when dismissible.
                • Save is always available even for previously-submitted
                  guests, in case anything has changed since last time. */}
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              {dismissible && (
                <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="qh-skip">
                  {previouslySubmitted ? "Not now" : "Skip for now"}
                </Button>
              )}
              {previouslySubmitted && (
                <Button
                  variant="outline"
                  onClick={() => { onSaved && onSaved({ submitted: true }); onOpenChange(false); }}
                  disabled={saving}
                  className="border-accent/60 text-accent hover:bg-accent/10"
                  data-testid="qh-reuse"
                >
                  Use my saved answers
                </Button>
              )}
              <Button
                onClick={submit}
                disabled={saving}
                className="bg-accent hover:bg-accent/90 text-accent-foreground"
                data-testid="qh-save"
              >
                {saving ? "Saving…" : previouslySubmitted ? "Update answers" : "Save preferences"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
